import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Box, CircularProgress, Stack } from '@mui/material'
import React from 'react'
import { useLatestState } from 'src/Libs/flow/advanced-settings/hooks'
import { useLocalStorageState } from 'src/Libs/flow/hooks'
import { ChargingMode, ChargingSchedule } from 'src/Libs/flow/installation/forms'
import { getEvseEfficientPvScheduleLocalStorageKey, getEvseSafePvScheduleLocalStorageKey } from 'src/Libs/flow/parsers'
import { AccordionItem } from '@e3dc-react/shell/Elements/Accordion'
import { BaseSelect as Select } from 'src/Libs/inputs/react-hook-form/Select'
import { ChargingCalendar } from './ChargingCalendar'
import { SettingInfoBox, SettingsBox } from 'src/Elements/Data'
import useModals from 'src/Hooks/useModals'
import { infoBoxContentStyle } from '../../constants'
import { useInstallationAccessRightsContext } from 'src/Libs/installation-rights-management/provider/InstallationAccessRightsContext'
import { useAppRatingContext } from 'src/Libs/app-rating/hooks'

const CHARGING_PRIORITY_OPTIONS = [
  {
    value: 'pvOnly',
    labelKey: 'user-settings.charging-priority-page.options.solar-only',
    descriptionKey: 'user-settings.charging-priority-page.options.solar-only-description',
  },
  {
    value: 'pvImmediate',
    labelKey: 'user-settings.charging-priority-page.options.solar-min-charge',
    descriptionKey: 'user-settings.charging-priority-page.options.solar-min-charge-description',
  },
  {
    value: 'pvDelayed',
    labelKey: 'user-settings.charging-priority-page.options.solar-delayed',
    descriptionKey: 'user-settings.charging-priority-page.options.solar-delayed-description',
  },
  {
    value: 'standard',
    labelKey: 'user-settings.charging-priority-page.options.boost',
    descriptionKey: 'user-settings.charging-priority-page.options.boost-description',
  },
]

type ChargingPriorityContentProps = {
  accordionTitle: string
  currentChargingMode: ChargingMode
  deviceId: string
  safePvSchedule: ChargingSchedule
  efficientPvSchedule: ChargingSchedule
  isLoading: boolean
  onChargingModeChange: (mode: ChargingMode, schedules: { safePv: ChargingSchedule; efficientPv: ChargingSchedule }) => void
  onSafePvScheduleChange: (schedule: ChargingSchedule) => void
  onEfficientPvScheduleChange: (schedule: ChargingSchedule) => void
}

export const ChargingPriorityContent: React.FC<ChargingPriorityContentProps> = ({
  accordionTitle,
  currentChargingMode,
  deviceId,
  safePvSchedule,
  efficientPvSchedule,
  isLoading,
  onChargingModeChange,
  onSafePvScheduleChange,
  onEfficientPvScheduleChange,
}) => {
  const { t } = useCustomTranslation()
  const { triggerAppRating } = useAppRatingContext()
  const { hasInstallationWritingRights: userCanEdit } = useInstallationAccessRightsContext()
  const { openChargingScheduleEditorDialog } = useModals()

  const [chargingMode, setChargingMode] = useLatestState<ChargingMode>(currentChargingMode)

  const disableLocalStorage = !deviceId

  const [pvImmediateCalendar, setPvImmediateCalendar] = useLocalStorageState({
    key: getEvseSafePvScheduleLocalStorageKey(deviceId),
    initialValue: safePvSchedule,
    disableSave: disableLocalStorage,
  })

  const [pvDelayedCalendar, setPvDelayedCalendar] = useLocalStorageState({
    key: getEvseEfficientPvScheduleLocalStorageKey(deviceId),
    initialValue: efficientPvSchedule,
    disableSave: disableLocalStorage,
  })

  const updateChargingMode = (newMode: ChargingMode): void => {
    if (newMode === chargingMode) return

    setChargingMode(newMode)
    onChargingModeChange(newMode, { safePv: pvImmediateCalendar, efficientPv: pvDelayedCalendar })
  }

  const handleSavePvImmediateSchedule = (newSchedule: ChargingSchedule): void => {
    setPvImmediateCalendar(newSchedule)
    onSafePvScheduleChange(newSchedule)
  }

  const handleSavePvDelayedSchedule = (newSchedule: ChargingSchedule): void => {
    setPvDelayedCalendar(newSchedule)
    onEfficientPvScheduleChange(newSchedule)
  }

  const isDisabled = isLoading || !userCanEdit
  const selectedConfig = CHARGING_PRIORITY_OPTIONS.find(opt => opt.value === chargingMode) ?? CHARGING_PRIORITY_OPTIONS[0]

  const selectOptions = CHARGING_PRIORITY_OPTIONS.map(option => ({
    value: option.value,
    label: t(option.labelKey),
  }))

  const isChargingModePvImmediate = chargingMode === 'pvImmediate'
  const isChargingModePvDelayed = chargingMode === 'pvDelayed'

  return (
    <AccordionItem title={accordionTitle} expanded accordionDetailsSx={{ px: 4, py: 2 }}>
      <Stack>
        <SettingsBox
          title={accordionTitle}
          suffix={
            <Box sx={{ minWidth: { xs: '100%', sm: 300 } }}>
              <Select
                options={selectOptions}
                value={chargingMode}
                onChange={e => {
                  updateChargingMode(e.target.value as ChargingMode)
                  triggerAppRating()
                }}
                disabled={isDisabled}
                fullWidth
              />
            </Box>
          }
        />
        <SettingInfoBox title={t(selectedConfig.labelKey)} description={t(selectedConfig.descriptionKey)}>
          {isChargingModePvImmediate || isChargingModePvDelayed ? (
            <Box sx={infoBoxContentStyle}>
              <ChargingCalendar
                schedule={isChargingModePvImmediate ? pvImmediateCalendar : pvDelayedCalendar}
                includeTime={!isChargingModePvImmediate}
                onClickEditCalendar={() =>
                  openChargingScheduleEditorDialog({
                    initialSchedule: isChargingModePvImmediate ? pvImmediateCalendar : pvDelayedCalendar,
                    includeTime: !isChargingModePvImmediate,
                    saveSchedule: newValue => {
                      if (isChargingModePvImmediate) handleSavePvImmediateSchedule(newValue)
                      else handleSavePvDelayedSchedule(newValue)
                      triggerAppRating()
                    },
                    isLoading: isLoading,
                  })
                }
              />
            </Box>
          ) : null}
        </SettingInfoBox>
      </Stack>
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress />
        </Box>
      )}
    </AccordionItem>
  )
}
